// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");

import type { CanonicalProjectEvent } from '@web/lib/projector';
import type {
  ActivityItem,
  ActivityRunStatus,
} from '@web/lib/remoteActivityModel';

export type RemoteExecutionMode = 'single-agent' | 'workforce';

export type AgentExecutionState =
  | 'queued'
  | 'running'
  | 'waiting'
  | 'completed'
  | 'failed'
  | 'interrupted';

export type AgentMessage = {
  id: string;
  exchangeId: string | null;
  direction: 'assignment' | 'response';
  content: string;
  eventIds: string[];
};

export type AgentToolCall = {
  id: string;
  name: string;
  detail: string;
  state: AgentExecutionState;
  eventIds: string[];
};

export type AgentExecutionNode = {
  id: string;
  parentId: string | null;
  agentId: string | null;
  name: string;
  role: string;
  state: AgentExecutionState;
  parallelGroupId: string | null;
  childIds: string[];
  assignmentIds: string[];
  responseIds: string[];
  toolCallIds: string[];
  itemIds: string[];
  eventIds: string[];
  sequence: number;
  startedAt: string | null;
  updatedAt: string | null;
};

export type AgentExecutionForest = {
  mode: RemoteExecutionMode;
  rootIds: string[];
  nodesById: Record<string, AgentExecutionNode>;
  messagesById: Record<string, AgentMessage>;
  toolsById: Record<string, AgentToolCall>;
  unassignedItemIds: string[];
};

type BuildAgentForestInput = {
  runId: string;
  mode: RemoteExecutionMode;
  runStatus: ActivityRunStatus;
  items: ActivityItem[];
  canonicalEvents: CanonicalProjectEvent[];
};

const AGENT_ID_KEYS = [
  'agent_execution_id',
  'agentExecutionId',
  'execution_id',
  'executionId',
];
const TARGET_AGENT_ID_KEYS = [
  'target_agent_execution_id',
  'targetAgentExecutionId',
  'target_execution_id',
  'targetExecutionId',
];
const SOURCE_AGENT_ID_KEYS = [
  'source_agent_execution_id',
  'sourceAgentExecutionId',
  'source_execution_id',
  'sourceExecutionId',
];
const PARENT_ID_KEYS = [
  'parent_agent_execution_id',
  'parentAgentExecutionId',
  'parent_execution_id',
  'parentExecutionId',
];
const ASSIGNMENT_KEYS = [
  'query',
  'prompt',
  'instruction',
  'task',
  'input',
  'assignment',
  'request',
  'content',
  'message',
  'description',
];
const RESPONSE_KEYS = [
  'response',
  'output',
  'answer',
  'result',
  'summary',
  'content',
  'message',
];

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function payloadRecords(value: unknown): Record<string, unknown>[] {
  const payload = asRecord(value);
  return [
    payload,
    asRecord(payload.context),
    asRecord(payload.metadata),
    asRecord(payload.agent),
    asRecord(payload.source_agent),
    asRecord(payload.target_agent),
    asRecord(payload.tool),
  ];
}

function textFrom(
  value: unknown,
  keys: readonly string[],
  records = payloadRecords(value)
): string {
  for (const record of records) {
    for (const key of keys) {
      const candidate = record[key];
      if (
        (typeof candidate === 'string' || typeof candidate === 'number') &&
        String(candidate).trim()
      ) {
        return String(candidate).trim();
      }
    }
  }
  return '';
}

function normalizeType(value: string): string {
  return value.toLowerCase().replace(/[-\s]+/g, '_');
}

function eventAgentId(
  payload: unknown,
  direction?: AgentMessage['direction']
): string {
  if (direction === 'assignment') {
    return textFrom(payload, [
      ...AGENT_ID_KEYS,
      ...TARGET_AGENT_ID_KEYS,
      ...SOURCE_AGENT_ID_KEYS,
    ]);
  }
  if (direction === 'response') {
    return textFrom(payload, [
      ...AGENT_ID_KEYS,
      ...SOURCE_AGENT_ID_KEYS,
      ...TARGET_AGENT_ID_KEYS,
    ]);
  }
  return textFrom(payload, [
    ...AGENT_ID_KEYS,
    ...TARGET_AGENT_ID_KEYS,
    ...SOURCE_AGENT_ID_KEYS,
  ]);
}

function stateFromType(type: string): AgentExecutionState {
  const normalized = normalizeType(type);
  if (/fail|error|block/.test(normalized)) return 'failed';
  if (/interrupt|cancel/.test(normalized)) return 'interrupted';
  if (/wait|pause|input_required/.test(normalized)) return 'waiting';
  if (/complete|finish|deactivate|succeed/.test(normalized)) {
    return 'completed';
  }
  if (
    /create|queue|assign/.test(normalized) &&
    !/start|activate/.test(normalized)
  ) {
    return 'queued';
  }
  return 'running';
}

const STATE_PRIORITY: Record<AgentExecutionState, number> = {
  queued: 0,
  running: 1,
  waiting: 2,
  completed: 3,
  interrupted: 4,
  failed: 5,
};

function isAgentLifecycle(type: string): boolean {
  const normalized = normalizeType(type);
  if (/query|response|message|tool|function_call/.test(normalized)) {
    return false;
  }
  return (
    /agent|execution/.test(normalized) &&
    /create|queue|assign|start|activate|wait|pause|complete|finish|deactivate|fail|error|interrupt|cancel/.test(
      normalized
    )
  );
}

function messageDirection(type: string): AgentMessage['direction'] | null {
  const normalized = normalizeType(type);
  if (/response|answer|output/.test(normalized)) return 'response';
  if (/query|request|prompt|assignment|instruction|input/.test(normalized)) {
    return 'assignment';
  }
  return null;
}

function isToolEvent(type: string): boolean {
  return /tool|toolkit|function_call/.test(normalizeType(type));
}

function isExplicitAgentExchange(payload: unknown): boolean {
  const exchangeKind = textFrom(payload, [
    'exchange_kind',
    'exchangeKind',
    'message_kind',
    'messageKind',
    'channel',
  ]);
  if (/agent.?to.?agent|inter.?agent|delegation/i.test(exchangeKind)) {
    return true;
  }
  return Boolean(
    textFrom(payload, SOURCE_AGENT_ID_KEYS) &&
    textFrom(payload, TARGET_AGENT_ID_KEYS)
  );
}

function addUnique(items: string[], value: string): void {
  if (value && !items.includes(value)) items.push(value);
}

function forestSort(
  left: AgentExecutionNode,
  right: AgentExecutionNode
): number {
  if (left.sequence !== right.sequence) return left.sequence - right.sequence;
  if (left.startedAt && right.startedAt && left.startedAt !== right.startedAt) {
    return left.startedAt.localeCompare(right.startedAt);
  }
  return left.id.localeCompare(right.id);
}

export function buildAgentExecutionForest({
  runId,
  mode,
  runStatus,
  items,
  canonicalEvents,
}: BuildAgentForestInput): AgentExecutionForest {
  const nodesById: Record<string, AgentExecutionNode> = {};
  const messagesById: Record<string, AgentMessage> = {};
  const toolsById: Record<string, AgentToolCall> = {};
  const claimedItemIds = new Set<string>();
  const canonicalOwnerByEventId = new Map<string, string>();
  let fallbackSequence = 1_000_000;

  const ensureNode = (
    id: string,
    update: Partial<AgentExecutionNode> = {}
  ): AgentExecutionNode => {
    const existing = nodesById[id];
    if (!existing) {
      nodesById[id] = {
        id,
        parentId: update.parentId ?? null,
        agentId: update.agentId ?? null,
        name: update.name || 'Agent',
        role: update.role || '',
        state: update.state || 'queued',
        parallelGroupId: update.parallelGroupId ?? null,
        childIds: [],
        assignmentIds: [],
        responseIds: [],
        toolCallIds: [],
        itemIds: [],
        eventIds: [],
        sequence: update.sequence ?? fallbackSequence++,
        startedAt: update.startedAt ?? null,
        updatedAt: update.updatedAt ?? null,
      };
      return nodesById[id];
    }
    if (update.parentId !== undefined) existing.parentId = update.parentId;
    if (update.agentId) existing.agentId = update.agentId;
    if (update.name && (existing.name === 'Agent' || update.name !== 'Agent')) {
      existing.name = update.name;
    }
    if (update.role) existing.role = update.role;
    if (update.parallelGroupId) {
      existing.parallelGroupId = update.parallelGroupId;
    }
    if (update.sequence !== undefined) {
      existing.sequence = Math.min(existing.sequence, update.sequence);
    }
    if (update.startedAt && !existing.startedAt)
      existing.startedAt = update.startedAt;
    if (update.updatedAt) existing.updatedAt = update.updatedAt;
    if (update.state) {
      const existingTerminal = STATE_PRIORITY[existing.state] >= 3;
      const updateTerminal = STATE_PRIORITY[update.state] >= 3;
      if (
        !existingTerminal ||
        (updateTerminal &&
          STATE_PRIORITY[update.state] >= STATE_PRIORITY[existing.state])
      ) {
        existing.state = update.state;
      }
    }
    return existing;
  };

  const describeNode = (
    payload: unknown,
    direction?: AgentMessage['direction']
  ): Partial<AgentExecutionNode> => {
    const nameKeys =
      direction === 'assignment'
        ? [
            'target_agent_name',
            'targetAgentName',
            'agent_name',
            'agentName',
            'name',
          ]
        : direction === 'response'
          ? [
              'source_agent_name',
              'sourceAgentName',
              'agent_name',
              'agentName',
              'name',
            ]
          : ['agent_name', 'agentName', 'name', 'agent'];
    const parentId = textFrom(payload, PARENT_ID_KEYS);
    return {
      ...(parentId ? { parentId } : {}),
      agentId: textFrom(payload, ['agent_id', 'agentId']) || null,
      name: textFrom(payload, nameKeys) || 'Agent',
      role:
        textFrom(payload, ['agent_role', 'agentRole', 'role', 'specialty']) ||
        '',
      parallelGroupId:
        textFrom(payload, ['parallel_group_id', 'parallelGroupId']) || null,
    };
  };

  const addMessage = (
    node: AgentExecutionNode,
    direction: AgentMessage['direction'],
    content: string,
    sourceId: string,
    payload: unknown,
    eventIds: string[]
  ): void => {
    if (!content) return;
    const id = `${sourceId}:${direction}`;
    if (!messagesById[id]) {
      messagesById[id] = {
        id,
        exchangeId:
          textFrom(payload, [
            'exchange_id',
            'exchangeId',
            'query_id',
            'queryId',
          ]) || null,
        direction,
        content,
        eventIds,
      };
    }
    addUnique(
      direction === 'assignment' ? node.assignmentIds : node.responseIds,
      id
    );
  };

  const addTool = (
    node: AgentExecutionNode,
    sourceId: string,
    type: string,
    payload: unknown,
    eventIds: string[],
    fallbackName = ''
  ): void => {
    const explicitId = textFrom(payload, ['tool_call_id', 'toolCallId']);
    const id = explicitId || `${node.id}:tool:${sourceId}`;
    const existing = toolsById[id];
    const name =
      textFrom(payload, [
        'tool_name',
        'toolName',
        'toolkit_name',
        'method_name',
        'function_name',
        'name',
      ]) ||
      fallbackName ||
      'Tool';
    const detail = textFrom(payload, [
      'detail',
      'description',
      'input',
      'query',
    ]);
    toolsById[id] = existing
      ? {
          ...existing,
          name: existing.name === 'Tool' ? name : existing.name,
          detail: detail || existing.detail,
          state: stateFromType(type),
          eventIds: Array.from(new Set([...existing.eventIds, ...eventIds])),
        }
      : {
          id,
          name,
          detail,
          state: stateFromType(type),
          eventIds,
        };
    addUnique(node.toolCallIds, id);
  };

  for (const event of canonicalEvents) {
    if (event.runId !== runId) continue;
    const direction = messageDirection(event.eventType);
    const executionId = eventAgentId(event.payload, direction || undefined);
    if (!executionId) continue;
    canonicalOwnerByEventId.set(event.eventId, executionId);
    const node = ensureNode(executionId, {
      ...describeNode(event.payload, direction || undefined),
      sequence: event.runSequence,
      startedAt: event.createdAt,
      updatedAt: event.createdAt,
      state: isAgentLifecycle(event.eventType)
        ? stateFromType(event.eventType)
        : 'running',
    });
    addUnique(node.eventIds, event.eventId);
    if (node.parentId) ensureNode(node.parentId);

    if (direction) {
      addMessage(
        node,
        direction,
        textFrom(
          event.payload,
          direction === 'assignment' ? ASSIGNMENT_KEYS : RESPONSE_KEYS
        ),
        event.eventId,
        event.payload,
        [event.eventId]
      );
    } else {
      addMessage(
        node,
        'assignment',
        textFrom(event.payload, [
          'query',
          'prompt',
          'instruction',
          'assignment',
        ]),
        event.eventId,
        event.payload,
        [event.eventId]
      );
      addMessage(
        node,
        'response',
        textFrom(event.payload, ['response', 'output', 'answer']),
        event.eventId,
        event.payload,
        [event.eventId]
      );
    }
    if (isToolEvent(event.eventType)) {
      addTool(node, event.eventId, event.eventType, event.payload, [
        event.eventId,
      ]);
    }
  }

  const legacyAgentIds: string[] = [];
  for (const item of items) {
    const explicitExchange = isExplicitAgentExchange(item.payload);
    const exchangeDirection =
      item.kind === 'question'
        ? 'assignment'
        : item.kind === 'user'
          ? 'response'
          : null;
    const canonicalOwnerId = item.eventIds
      .map((eventId) => canonicalOwnerByEventId.get(eventId))
      .find(Boolean);
    const explicitId =
      eventAgentId(
        item.payload,
        explicitExchange && exchangeDirection ? exchangeDirection : undefined
      ) ||
      canonicalOwnerId ||
      '';
    const projectedFromCanonical = Boolean(canonicalOwnerId);
    if (item.kind === 'agent') {
      const description = describeNode(item.payload);
      let id = explicitId;
      if (!id && item.type === 'deactivate_agent') {
        const name = description.name || 'Agent';
        const candidates = legacyAgentIds.filter(
          (candidate) => nodesById[candidate]?.name === name
        );
        id = candidates.length === 1 ? candidates[0] : '';
      }
      if (!id) id = `${runId}:legacy-agent:${item.id}`;
      const node = ensureNode(id, {
        ...description,
        sequence: item.sequence,
        startedAt: item.timestamp,
        updatedAt: item.timestamp,
        state: stateFromType(item.type),
      });
      if (!explicitId && !legacyAgentIds.includes(id)) legacyAgentIds.push(id);
      addUnique(node.itemIds, item.id);
      item.eventIds.forEach((eventId) => addUnique(node.eventIds, eventId));
      claimedItemIds.add(item.id);
      if (node.parentId) ensureNode(node.parentId);
      if (
        !projectedFromCanonical &&
        /create|activate|start/.test(normalizeType(item.type))
      ) {
        addMessage(
          node,
          'assignment',
          textFrom(item.payload, ASSIGNMENT_KEYS),
          item.id,
          item.payload,
          item.eventIds
        );
      }
      if (
        !projectedFromCanonical &&
        /deactivate|complete|finish/.test(normalizeType(item.type))
      ) {
        addMessage(
          node,
          'response',
          textFrom(item.payload, RESPONSE_KEYS),
          item.id,
          item.payload,
          item.eventIds
        );
      }
      continue;
    }

    if (
      explicitId &&
      explicitExchange &&
      exchangeDirection &&
      (item.kind === 'question' || item.kind === 'user')
    ) {
      const node = ensureNode(explicitId, {
        ...describeNode(item.payload, exchangeDirection),
        sequence: item.sequence,
        updatedAt: item.timestamp,
        state: exchangeDirection === 'assignment' ? 'running' : undefined,
      });
      addMessage(
        node,
        exchangeDirection,
        textFrom(
          item.payload,
          exchangeDirection === 'assignment' ? ASSIGNMENT_KEYS : RESPONSE_KEYS
        ),
        item.id,
        item.payload,
        item.eventIds
      );
      addUnique(node.itemIds, item.id);
      claimedItemIds.add(item.id);
      continue;
    }

    if (!explicitId || item.kind === 'question' || item.kind === 'user') {
      continue;
    }
    const node = ensureNode(explicitId, {
      ...describeNode(item.payload),
      sequence: item.sequence,
      updatedAt: item.timestamp,
      state: item.state === 'waiting' ? 'waiting' : undefined,
    });
    addUnique(node.itemIds, item.id);
    item.eventIds.forEach((eventId) => addUnique(node.eventIds, eventId));
    claimedItemIds.add(item.id);
    if (item.kind === 'tool') {
      addTool(
        node,
        item.id,
        item.type,
        item.payload,
        item.eventIds,
        item.detail
      );
    }
  }

  const existingNodes = Object.values(nodesById);
  const realNodes = existingNodes.filter(
    (node) =>
      node.eventIds.length > 0 ||
      node.itemIds.length > 0 ||
      node.assignmentIds.length > 0 ||
      node.responseIds.length > 0 ||
      node.toolCallIds.length > 0
  );
  if (mode === 'single-agent' && realNodes.length === 1) {
    const soleAgent = realNodes[0];
    for (const item of items) {
      if (
        claimedItemIds.has(item.id) ||
        item.kind === 'question' ||
        item.kind === 'user' ||
        item.kind === 'result'
      ) {
        continue;
      }
      addUnique(soleAgent.itemIds, item.id);
      claimedItemIds.add(item.id);
      if (item.kind === 'tool') {
        addTool(
          soleAgent,
          item.id,
          item.type,
          item.payload,
          item.eventIds,
          item.detail
        );
      }
    }
  }

  for (const node of Object.values(nodesById)) {
    const seen = new Set([node.id]);
    let parentId = node.parentId;
    while (parentId && nodesById[parentId]) {
      if (seen.has(parentId)) {
        node.parentId = null;
        break;
      }
      seen.add(parentId);
      parentId = nodesById[parentId].parentId;
    }
  }

  for (const node of Object.values(nodesById)) node.childIds = [];
  for (const node of Object.values(nodesById)) {
    if (!node.parentId || node.parentId === node.id) continue;
    const parent = nodesById[node.parentId];
    if (parent) addUnique(parent.childIds, node.id);
  }

  const terminalRun = runStatus !== 'running' && runStatus !== 'waiting_input';
  if (terminalRun) {
    for (const node of Object.values(nodesById)) {
      if (
        node.state === 'queued' ||
        node.state === 'running' ||
        node.state === 'waiting'
      ) {
        node.state =
          runStatus === 'failed'
            ? 'failed'
            : runStatus === 'interrupted' || runStatus === 'cancelled'
              ? 'interrupted'
              : 'completed';
      }
    }
  }

  for (const node of Object.values(nodesById)) {
    node.childIds.sort((left, right) =>
      forestSort(nodesById[left], nodesById[right])
    );
  }
  const rootIds = Object.values(nodesById)
    .filter(
      (node) =>
        !node.parentId || !nodesById[node.parentId] || node.parentId === node.id
    )
    .sort(forestSort)
    .map((node) => node.id);

  return {
    mode,
    rootIds,
    nodesById,
    messagesById,
    toolsById,
    unassignedItemIds: items
      .filter((item) => !claimedItemIds.has(item.id))
      .map((item) => item.id),
  };
}
